import type { Survey } from '../../fetchSurvey/types/survey';
import { evaluateDisplayLogic } from '../evaluator/evaluateDisplayLogic';
import type { LogicContext } from '../types';

export type EndPageMatchType = 'conditional' | 'default' | 'fallback';

export type EndPageResolution = {
  endPageId: string | undefined;
  matchType: EndPageMatchType;
};

/**
 * Picks the end page to show after submit:
 * 1. First conditional page (has displayLogic) whose condition matches
 * 2. Else first page without displayLogic (default thank-you page)
 * 3. Else endPages[0] (safety fallback)
 */
export function resolveEndPageResolution(
  survey: Survey | null | undefined,
  context: LogicContext
): EndPageResolution {
  const endPages = survey?.endPages.filter(page => !page.disabled) ?? [];
  if (endPages.length === 0) {
    return { endPageId: undefined, matchType: 'fallback' };
  }

  for (const page of endPages) {
    if (page.displayLogic && evaluateDisplayLogic(page.displayLogic, context)) {
      return { endPageId: page.id, matchType: 'conditional' };
    }
  }

  const defaultPage = endPages.find(page => !page.displayLogic);
  if (defaultPage) {
    return { endPageId: defaultPage.id, matchType: 'default' };
  }

  return { endPageId: endPages[0]?.id, matchType: 'fallback' };
}

export function resolveEndPageId(
  survey: Survey | null | undefined,
  context: LogicContext
): string | undefined {
  return resolveEndPageResolution(survey, context).endPageId;
}
